import { useState, useEffect } from 'react'
import { useUIStore } from '@/stores/ui.store'
import { useDlqItems, useRetryDlq, useDiscardDlq } from '@/hooks/useGovernance'
import {
    AlertTriangle, RefreshCw, Trash2, Loader2,
    Terminal, Calendar
} from 'lucide-react'
import { format } from 'date-fns'

export function DlqPage() {
    const setPageTitle = useUIStore((s) => s.setPageTitle)
    const [expandedId, setExpandedId] = useState<string | null>(null)

    useEffect(() => { setPageTitle('Queue de Erros (DLQ)') }, [setPageTitle])

    const { data: items, isLoading } = useDlqItems()
    const retryMut = useRetryDlq()
    const discardMut = useDiscardDlq()

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-xl font-bold text-surface-900">Dead Letter Queue</h2>
                    <p className="text-sm text-surface-700/60 mt-0.5">Gerenciamento de falhas em processos assíncronos</p>
                </div>
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-danger-50 text-danger-600 border border-danger-100">
                    <AlertTriangle size={16} />
                    <span className="text-xs font-bold uppercase">{items?.filter(i => i.status === 'pendente').length || 0} Pendentes</span>
                </div>
            </div>

            {/* List */}
            <div className="glass-card overflow-hidden">
                {isLoading ? (
                    <div className="flex justify-center p-12">
                        <Loader2 size={24} className="animate-spin text-primary-600" />
                    </div>
                ) : items?.length === 0 ? (
                    <div className="text-center py-20 text-surface-700/60">
                        <div className="w-16 h-16 rounded-full bg-green-50 text-green-500 flex items-center justify-center mx-auto mb-4">
                            <span className="font-bold">✓</span>
                        </div>
                        <p className="text-lg font-bold text-surface-900">Tudo limpo por aqui!</p>
                        <p className="text-sm">Nenhuma falha pendente na fila de processamento.</p>
                    </div>
                ) : (
                    <div className="divide-y divide-surface-100">
                        {items?.map(item => (
                            <div key={item.id} className={`flex flex-col hover:bg-surface-50/50 transition-colors ${expandedId === item.id ? 'bg-surface-50' : ''}`}>
                                <div className="p-4 flex items-center justify-between">
                                    <div className="flex items-start gap-4 min-w-0">
                                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 
                                            ${item.status === 'pendente' ? 'bg-danger-100 text-danger-600' : 'bg-surface-100 text-surface-400'}`}>
                                            <Terminal size={18} />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <h3 className="text-sm font-bold text-surface-900 truncate uppercase">{item.evento}</h3>
                                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border uppercase
                                                    ${item.status === 'pendente' ? 'bg-danger-50 text-danger-700 border-danger-100' : 'bg-surface-100 text-surface-600 border-surface-200'}`}>
                                                    {item.status}
                                                </span>
                                            </div>
                                            <p className="text-xs text-danger-600 mt-0.5 font-medium line-clamp-1">{item.erro}</p>
                                            <div className="flex items-center gap-3 mt-1.5 text-[10px] text-surface-700/50 font-medium">
                                                <span className="flex items-center gap-1"><Calendar size={12} /> {format(new Date(item.created_at), "dd/MM HH:mm")}</span>
                                                <span className="flex items-center gap-1"><RefreshCw size={12} /> {item.tentativas}/{item.max_tentativas} tentativas</span>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2 shrink-0 ml-4">
                                        <button onClick={() => setExpandedId(expandedId === item.id ? null : item.id)}
                                            className="btn btn-sm btn-ghost gap-1.5">
                                            <Eye size={14} /> Detalhes
                                        </button>
                                        <button onClick={() => retryMut.mutate(item.id)} disabled={retryMut.isPending || item.status !== 'pendente'}
                                            className="btn btn-sm btn-primary gap-1.5">
                                            <RefreshCw size={14} className={retryMut.isPending ? 'animate-spin' : ''} /> Reprocessar
                                        </button>
                                        <button onClick={() => discardMut.mutate(item.id)} disabled={discardMut.isPending}
                                            className="p-2 text-surface-400 hover:text-danger-600 transition-colors">
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>

                                {expandedId === item.id && (
                                    <div className="px-4 pb-4 animate-in fade-in slide-in-from-top-2 duration-200">
                                        <div className="p-4 rounded-xl bg-surface-950 text-green-400 font-mono text-[11px] overflow-auto max-h-60 shadow-inner border border-white/5">
                                            <p className="text-white/40 mb-2 uppercase text-[9px] tracking-widest font-sans">Payload do Evento:</p>
                                            {JSON.stringify(item.payload, null, 2)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

function Eye({ size, className }: { size?: number, className?: string }) {
    return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0z" /><circle cx="12" cy="12" r="3" /></svg>
}
